import React, { useCallback, useEffect, useMemo, useState, FC, useId, useRef } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { useCssHandles } from '../../../shared/hooks/useCssHandles'
import { AdminLayout, AdminFilterTrigger, AdminFilterSidebar } from '../components'
import { Modal, ConfirmModal, ImageWithFallback, Select, Input, Textarea, Checkbox, TablePagination, PriceInput, FileInput, DecimalInput } from '../../../shared/components'
import { colors } from '../../../styles/design-system'
import { buildImageUrl } from '../../../config'
import { Battery } from '../../../types/battery'
import { PaginationResponse } from '../../../types/api'
import adminProductService from '../../../services/adminProductService'
import {
  AdminProductFormValues,
  AdminProductMetadata,
  AdminProductSortField,
  AdminProductStatusFilter
} from '../../../types/adminProducts'

const CSS_HANDLES = [
  'adminProducts',
  'productsHeader',
  'productsTitle',
  'productsSubtitle',
  'productsActions',
  'addProductButton',
  'searchInput',
  'filterSelect',
  'productsTable',
  'tableHeader',
  'tableRow',
  'productImage',
  'productInfo',
  'productName',
  'productBrand',
  'productPrice',
  'productStock',
  'productStatus',
  'productActions',
  'viewButton',
  'editButton',
  'deleteButton',
  'statusToggle',
  'statusActive',
  'statusInactive',
  'modal',
  'modalOverlay',
  'modalContent',
  'modalHeader',
  'modalTitle',
  'modalClose',
  'modalBody',
  'formGroup',
  'formLabel',
  'formInput',
  'formSelect',
  'formTextarea',
  'formRow',
  'modalFooter',
  'cancelButton',
  'saveButton',
  'pagination',
  'paginationButton',
  'paginationInfo'
] as const

const AdminProducts: FC = () => {
  const handles = useCssHandles(CSS_HANDLES)
  const [products, setProducts] = useState<Battery[]>([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingProduct, setEditingProduct] = useState<Battery | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [pagination, setPagination] = useState<PaginationResponse | null>(null)
  const [isFetching, setIsFetching] = useState(false)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(false)

  type ProductFilters = {
    search: string
    status: AdminProductStatusFilter
    name: string
    model: string
    brandId: string
    categoryId: string
    priceMin: string
    priceMax: string
    stockMin: string
    stockMax: string
    capacityMin: string
    capacityMax: string
    voltage: string
  }

  const defaultFilters: ProductFilters = {
    search: '',
    status: 'all',
    name: '',
    model: '',
    brandId: '',
    categoryId: '',
    priceMin: '',
    priceMax: '',
    stockMin: '',
    stockMax: '',
    capacityMin: '',
    capacityMax: '',
    voltage: ''
  }

  const [filters, setFilters] = useState<ProductFilters>(() => ({
    ...defaultFilters
  }))
  const [draftFilters, setDraftFilters] = useState<ProductFilters>(() => ({
    ...defaultFilters
  }))
  const [sortState, setSortState] = useState<{
    field: AdminProductSortField
    order: 'asc' | 'desc'
  }>({
    field: 'createdAt',
    order: 'desc'
  })

  const itemsPerPage = 10
  const [metadata, setMetadata] = useState<AdminProductMetadata | null>(null)
  const [isMetadataLoading, setIsMetadataLoading] = useState(false)
  const [metadataError, setMetadataError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [isSavingProduct, setIsSavingProduct] = useState(false)
  const [pendingStatusToggle, setPendingStatusToggle] = useState<string | null>(null)
  const [pendingDeletion, setPendingDeletion] = useState<string | null>(null)
  const [deleteProductConfirmOpen, setDeleteProductConfirmOpen] = useState(false)
  const [productToDelete, setProductToDelete] = useState<Battery | null>(null)
  const [deleteImageConfirmOpen, setDeleteImageConfirmOpen] = useState(false)
  const [imageToDelete, setImageToDelete] = useState<{ productId: string; imageId: string } | null>(null)

  // Refs para evitar chamadas duplicadas
  const hasLoadedMetadata = useRef(false)
  const previousFiltersRef = useRef<string | null>(null)
  const previousPageRef = useRef<number | null>(null)
  const previousSortStateRef = useRef<string | null>(null)
  const isLoadingRef = useRef(false)
  const lastRequestIdRef = useRef<string | null>(null)

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price)
  }

  const resolveErrorMessage = (err: unknown, fallback: string) => {
    if (err && typeof err === 'object') {
      const axiosLike = err as {
        response?: {
          data?: {
            message?: string
            error?: string
          }
        }
        message?: string
      }

      const apiMessage =
        axiosLike.response?.data?.message ?? axiosLike.response?.data?.error

      if (apiMessage) {
        return apiMessage
      }

      if (axiosLike.message) {
        return axiosLike.message
      }
    }

    if (err instanceof Error && err.message) {
      return err.message
    }

    return fallback
  }

  const fetchProducts = useCallback(
    async (page: number) => {
      // Verificar se já está carregando para evitar chamadas simultâneas
      if (isLoadingRef.current) {
        return
      }

      // Serializar filtros e sortState para comparação
      const filtersKey = JSON.stringify(filters)
      const sortStateKey = JSON.stringify({
        field: sortState.field,
        order: sortState.order
      })

      // Verificar se realmente mudou
      const filtersChanged = previousFiltersRef.current !== filtersKey
      const pageChanged = previousPageRef.current !== page
      const sortStateChanged = previousSortStateRef.current !== sortStateKey

      // Se nada mudou e já foi carregado, não fazer nova chamada
      if (!filtersChanged && !pageChanged && !sortStateChanged && previousFiltersRef.current !== null && previousFiltersRef.current === filtersKey) {
        return
      }

      // Criar ID único para esta requisição
      const requestId = `${Date.now()}-${Math.random()}`
      
      // Marcar como carregando ANTES de fazer qualquer coisa
      isLoadingRef.current = true
      lastRequestIdRef.current = requestId
      previousFiltersRef.current = filtersKey
      previousPageRef.current = page
      previousSortStateRef.current = sortStateKey

      setIsFetching(true)
      setError(null)
      
      try {
        // Verificar se ainda é a requisição mais recente antes de fazer a chamada
        if (lastRequestIdRef.current !== requestId) {
          return
        }
        const trimmedSearch = filters.search.trim()
        const includeInactive = filters.status !== 'active'
        const isActive =
          filters.status === 'active'
            ? true
            : filters.status === 'inactive'
            ? false
            : undefined

        // Construir query de busca: se houver busca geral, usar ela; senão, combinar nome e modelo
        let searchQuery = trimmedSearch
        if (!trimmedSearch && (filters.name.trim() || filters.model.trim())) {
          const searchParts: string[] = []
          if (filters.name.trim()) searchParts.push(filters.name.trim())
          if (filters.model.trim()) searchParts.push(filters.model.trim())
          searchQuery = searchParts.join(' ')
        }

        // Converter brandId e categoryId para slug/name para o backend
        // Usar metadata atual do estado, não da closure
        const currentMetadata = metadata
        const brandSlug = filters.brandId && currentMetadata
          ? currentMetadata.brands.find(b => b.id === filters.brandId)?.slug
          : undefined
        const categorySlug = filters.categoryId && currentMetadata
          ? currentMetadata.categories.find(c => c.id === filters.categoryId)?.slug
          : undefined

        const data = await adminProductService.list({
          search: searchQuery || undefined,
          page,
          limit: itemsPerPage,
          includeInactive,
          status: filters.status,
          isActive,
          sortBy: sortState.field,
          sortOrder: sortState.order,
          brand: brandSlug ? [brandSlug] : undefined,
          category: categorySlug ? [categorySlug] : undefined,
          priceMin: filters.priceMin ? parseFloat(filters.priceMin) : undefined,
          priceMax: filters.priceMax ? parseFloat(filters.priceMax) : undefined,
          stockMin: filters.stockMin ? parseInt(filters.stockMin, 10) : undefined,
          stockMax: filters.stockMax ? parseInt(filters.stockMax, 10) : undefined,
          capacityMin: filters.capacityMin ? parseFloat(filters.capacityMin) : undefined,
          capacityMax: filters.capacityMax ? parseFloat(filters.capacityMax) : undefined,
          voltage: filters.voltage && filters.voltage.trim() !== '' 
            ? (() => {
                const voltageNum = parseFloat(filters.voltage);
                return !isNaN(voltageNum) && voltageNum > 0 ? [voltageNum] : undefined;
              })()
            : undefined
        })
        setProducts(data.products)
        setPagination(data.pagination)
        setHasLoaded(true)
      } catch (err) {
        console.error('Erro ao carregar produtos:', err)
        setError('Não foi possível carregar os produtos. Tente novamente.')
        setProducts([])
        setPagination(null)
        // Resetar refs em caso de erro para permitir nova tentativa
        if (lastRequestIdRef.current === requestId) {
          previousFiltersRef.current = null
          previousPageRef.current = null
          previousSortStateRef.current = null
          lastRequestIdRef.current = null
        }
      } finally {
        // Só atualizar estado se ainda for a requisição mais recente
        if (lastRequestIdRef.current === requestId) {
          setIsFetching(false)
          isLoadingRef.current = false
        }
      }
    },
    [filters, itemsPerPage, sortState.field, sortState.order, metadata]
  )

  const loadMetadata = useCallback(async () => {
    // Evitar execuções duplicadas (especialmente no React Strict Mode)
    if (hasLoadedMetadata.current) {
      return
    }

    hasLoadedMetadata.current = true
    setIsMetadataLoading(true)
    setMetadataError(null)
    try {
      const data = await adminProductService.getMetadata()
      setMetadata(data)
    } catch (err) {
      console.error('Erro ao carregar metadados de produtos:', err)
      setMetadata(null)
      setMetadataError(
        'Não foi possível carregar as informações de marcas e categorias.'
      )
      // Resetar flag em caso de erro para permitir nova tentativa
      hasLoadedMetadata.current = false
    } finally {
      setIsMetadataLoading(false)
    }
  }, [])

  useEffect(() => {
    loadMetadata()
  }, [loadMetadata])

  useEffect(() => {
    // Só buscar produtos se metadata já foi carregado (ou se não precisar de metadata para os filtros atuais)
    // Isso evita chamadas desnecessárias quando metadata ainda está carregando
    const needsMetadata = filters.brandId || filters.categoryId
    if (!needsMetadata || metadata) {
      fetchProducts(currentPage)
    }
  }, [fetchProducts, currentPage, metadata, filters.brandId, filters.categoryId])

  const hasActiveFilters = 
    filters.search.trim() !== '' || 
    filters.status !== 'all' ||
    filters.name.trim() !== '' ||
    filters.model.trim() !== '' ||
    filters.brandId !== '' ||
    filters.categoryId !== '' ||
    filters.priceMin !== '' ||
    filters.priceMax !== '' ||
    filters.stockMin !== '' ||
    filters.stockMax !== '' ||
    filters.capacityMin !== '' ||
    filters.capacityMax !== '' ||
    filters.voltage !== ''

  const handleOpenFilters = () => {
    setDraftFilters({ ...filters })
    setIsFilterSidebarOpen(true)
  }

  const handleCloseFilters = () => {
    setIsFilterSidebarOpen(false)
  }

  const handleDraftFilterChange = <Key extends keyof typeof draftFilters>(key: Key, value: (typeof draftFilters)[Key]) => {
    setDraftFilters((prev) => ({
      ...prev,
      [key]: value
    }))
  }

  const handleApplyFilters = () => {
    setFilters({ ...draftFilters })
    setCurrentPage(1)
    setIsFilterSidebarOpen(false)
  }

  const handleClearFilters = () => {
    const reset = { ...defaultFilters }
    setDraftFilters(reset)
    setFilters(reset)
    setCurrentPage(1)
  }

  const handleSort = (field: AdminProductSortField) => {
    setSortState((previous) => {
      const nextOrder = previous.field === field && previous.order === 'asc' ? 'desc' : 'asc'
      return {
        field,
        order: previous.field === field ? nextOrder : 'asc'
      }
    })
    setCurrentPage(1)
  }

  const renderSortIndicator = (field: AdminProductSortField) => {
    if (sortState.field !== field) {
      return (
        <span style={{ marginLeft: 6, color: colors.neutral.gray300 }} aria-hidden="true">
          ⇅
        </span>
      )
    }

    return (
      <span
        style={{
          marginLeft: 6,
          color: colors.primary.blue,
          display: 'inline-flex',
          alignItems: 'center'
        }}
        aria-hidden="true"
      >
        {sortState.order === 'asc' ? '↑' : '↓'}
      </span>
    )
  }

  const getAriaSort = (
    field: AdminProductSortField
  ): 'none' | 'ascending' | 'descending' => {
    if (sortState.field !== field) {
      return 'none'
    }
    return sortState.order === 'asc' ? 'ascending' : 'descending'
  }

  const handleAddProduct = () => {
    setActionError(null)
    setEditingProduct(null)
    setIsModalOpen(true)
  }

  const handleEditProduct = (product: Battery) => {
    setActionError(null)
    setEditingProduct(product)
    setIsModalOpen(true)
  }

  const handleDeleteProduct = (product: Battery) => {
    setProductToDelete(product)
    setDeleteProductConfirmOpen(true)
  }

  const confirmDeleteProduct = async () => {
    if (!productToDelete) return

    setPendingDeletion(productToDelete.id)
    setActionError(null)

    try {
      await adminProductService.remove(productToDelete.id)

      const isLastItemOnPage = products.length === 1 && currentPage > 1
      const nextPage = isLastItemOnPage ? currentPage - 1 : currentPage

      if (isLastItemOnPage) {
        setCurrentPage(nextPage)
      }

      await fetchProducts(nextPage)
      
      // Fechar o modal apenas após sucesso
      setDeleteProductConfirmOpen(false)
      setProductToDelete(null)
    } catch (err) {
      console.error('Erro ao excluir produto:', err)
      setActionError(
        resolveErrorMessage(
          err,
          'Não foi possível excluir o produto. Tente novamente.'
        )
      )
    } finally {
      setPendingDeletion(null)
    }
  }

  const handleDeleteImageFromModal = async (imageId: string): Promise<void> => {
    if (!editingProduct?.id) return
    setImageToDelete({ productId: editingProduct.id, imageId })
    setDeleteImageConfirmOpen(true)
    // A Promise será resolvida quando o usuário confirmar a exclusão
    // O recarregamento das imagens será feito no confirmDeleteImage
  }

  const confirmDeleteImage = async () => {
    if (!imageToDelete || !editingProduct?.id) return
    
    try {
      await adminProductService.deleteImage(imageToDelete.productId, imageToDelete.imageId)
      // Recarregar os detalhes do produto para atualizar as imagens no modal
      if (editingProduct.id === imageToDelete.productId) {
        // Recarregar o produto completo para atualizar as imagens no modal
        const updatedProductDetail = await adminProductService.getById(editingProduct.id)
        // Atualizar o editingProduct com os dados atualizados
        // Isso fará o modal recarregar as imagens através do useEffect
        setEditingProduct(updatedProductDetail.product)
      }
      setDeleteImageConfirmOpen(false)
      setImageToDelete(null)
    } catch (error) {
      console.error('Erro ao deletar imagem:', error)
      setActionError('Erro ao deletar imagem. Tente novamente.')
      setDeleteImageConfirmOpen(false)
      setImageToDelete(null)
    }
  }

  const handleToggleStatus = async (productId: string, currentStatus: boolean) => {
    setPendingStatusToggle(productId)
    setActionError(null)

    try {
      await adminProductService.updateStatus(productId, !currentStatus)
      await fetchProducts(currentPage)
    } catch (err) {
      console.error('Erro ao atualizar status do produto:', err)
      setActionError(
        resolveErrorMessage(
          err,
          'Não foi possível atualizar o status do produto.'
        )
      )
    } finally {
      setPendingStatusToggle(null)
    }
  }

  const handleSaveProduct = async (formValues: AdminProductFormValues, imageFile?: File) => {
    setIsSavingProduct(true)
    setActionError(null)

    try {
      if (editingProduct) {
        await adminProductService.update(editingProduct.id, formValues)
        // Forçar recarregamento resetando as refs de controle
        previousFiltersRef.current = null
        previousPageRef.current = null
        previousSortStateRef.current = null
        lastRequestIdRef.current = null
        isLoadingRef.current = false
        await fetchProducts(currentPage)
      } else {
        await adminProductService.create(formValues, imageFile)
        // Forçar recarregamento resetando as refs de controle
        previousFiltersRef.current = null
        previousPageRef.current = null
        previousSortStateRef.current = null
        lastRequestIdRef.current = null
        isLoadingRef.current = false
        setCurrentPage(1)
        await fetchProducts(1)
      }

      setIsModalOpen(false)
      setEditingProduct(null)
    } catch (err) {
      console.error('Erro ao salvar produto:', err)
      const message = resolveErrorMessage(
        err,
        'Não foi possível salvar o produto. Verifique os dados e tente novamente.'
      )
      setActionError(message)
      throw err
    } finally {
      setIsSavingProduct(false)
    }
  }

  const isApplyDisabled = JSON.stringify(draftFilters) === JSON.stringify(filters)

  const renderSkeleton = () => (
    <AdminLayout>
      <div className="space-y-8">
        <div className="space-y-3">
          <div className="h-8 w-64 animate-pulse rounded bg-gray-200" />
          <div className="h-4 w-80 animate-pulse rounded bg-gray-200" />
        </div>

        <div className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="h-10 w-full animate-pulse rounded bg-gray-200 md:w-80" />
            <div className="h-10 w-full animate-pulse rounded bg-gray-200 md:w-48" />
            <div className="h-10 w-full animate-pulse rounded bg-gray-200 md:w-48" />
          </div>
        </div>

        <div className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
          <div className="mb-4 h-6 w-48 animate-pulse rounded bg-gray-200" />
          <div className="space-y-3">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={`skeleton-row-${index}`} className="h-12 animate-pulse rounded bg-gray-100" />
            ))}
          </div>
        </div>
      </div>
    </AdminLayout>
  )

  if (!hasLoaded && isFetching) {
    return renderSkeleton()
  }

  if (error && !hasLoaded && !isFetching) {
    return (
      <AdminLayout>
        <div className="flex min-h-[50vh] items-center justify-center">
          <div className="space-y-4 text-center">
            <p className="text-gray-600">{error}</p>
            <button
              onClick={() => fetchProducts(1)}
              className="rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700"
            >
              Tentar novamente
            </button>
          </div>
        </div>
      </AdminLayout>
    )
  }

  const totalPages = pagination?.totalPages ?? 1
  const totalItems = pagination?.total ?? products.length
  const currentPageNumber = pagination?.page ?? currentPage
  const limit = pagination?.limit ?? itemsPerPage
  const startIndex = totalItems === 0 ? 0 : (currentPageNumber - 1) * limit
  const endIndex = totalItems === 0 ? 0 : Math.min(startIndex + products.length, totalItems)
  const showLoadingOverlay = hasLoaded && isFetching
  const activeFiltersDescriptions = [
    filters.status !== 'all' ? `status: ${filters.status === 'active' ? 'ativos' : 'inativos'}` : null,
    filters.search.trim() ? `busca: "${filters.search.trim()}"` : null,
    filters.name.trim() ? `nome: "${filters.name.trim()}"` : null,
    filters.model.trim() ? `modelo: "${filters.model.trim()}"` : null,
    filters.brandId ? `marca: ${metadata?.brands.find(b => b.id === filters.brandId)?.name || filters.brandId}` : null,
    filters.categoryId ? `categoria: ${metadata?.categories.find(c => c.id === filters.categoryId)?.name || filters.categoryId}` : null,
    filters.priceMin || filters.priceMax ? `preço: ${filters.priceMin || '0'} - ${filters.priceMax || '∞'}` : null,
    filters.stockMin || filters.stockMax ? `estoque: ${filters.stockMin || '0'} - ${filters.stockMax || '∞'}` : null,
    filters.capacityMin || filters.capacityMax ? `capacidade: ${filters.capacityMin || '0'} - ${filters.capacityMax || '∞'}Ah` : null,
    filters.voltage ? `voltagem: ${filters.voltage}V` : null
  ]
    .filter(Boolean)
    .join(' • ')

  return (
    <>
    <AdminLayout>
      <div className="relative">
        {showLoadingOverlay && (
          <div className="absolute inset-0 z-40 flex items-center justify-center bg-white/60">
            <div className="h-10 w-10 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
          </div>
        )}

        <div className={`${handles.adminProducts} min-h-screen bg-gray-50`}>
          <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
            {/* Header */}
            <div className={`${handles.productsHeader}`}>
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                  <h1 className={`${handles.productsTitle} text-3xl font-bold text-gray-900 mb-2`}>
                    Gestão de Produtos
                  </h1>
                  <p className={`${handles.productsSubtitle} text-lg text-gray-600`}>
                    Gerencie seu catálogo de baterias
                  </p>
                </div>
                <button
                  onClick={handleAddProduct}
                  disabled={isMetadataLoading || !metadata}
                  className={`${handles.addProductButton} inline-flex items-center justify-center rounded-lg bg-blue-600 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60`}
                >
                  {isMetadataLoading && !metadata ? 'Carregando...' : 'Adicionar Produto'}
                </button>
              </div>
            </div>

            {metadataError && (
              <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-800">
                <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                  <span>{metadataError}</span>
                  <button
                    onClick={() => loadMetadata()}
                    className="inline-flex items-center text-yellow-800 underline hover:text-yellow-900"
                  >
                    Tentar novamente
                  </button>
                </div>
              </div>
            )}

            {actionError && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                {actionError}
              </div>
            )}

            {error && hasLoaded && !isFetching && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                  <span>{error}</span>
                  <button
                    onClick={() => fetchProducts(currentPageNumber)}
                    className="inline-flex items-center text-red-700 underline hover:text-red-800"
                  >
                    Tentar novamente
                  </button>
                </div>
              </div>
            )}

            {/* Barra de ações */}
            <div className={`${handles.productsActions} rounded-xl bg-white p-6 shadow-sm`}>
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="text-sm text-gray-600">
                    Exibindo{' '}
                    <span className="font-semibold text-gray-900">
                      {totalItems === 0 ? 0 : startIndex + 1} - {endIndex}
                    </span>{' '}
                    de{' '}
                    <span className="font-semibold text-gray-900">{totalItems}</span>
                    {' '}produtos
                  </p>
                  {hasActiveFilters && (
                    <p className="mt-1 text-xs text-blue-600">
                      Filtros aplicados: {activeFiltersDescriptions}
                    </p>
                  )}
                </div>
                <AdminFilterTrigger
                  onClick={handleOpenFilters}
                  hasActiveFilters={hasActiveFilters}
                />
              </div>
            </div>

            {/* Tabela de Produtos */}
            <div className="rounded-lg bg-white shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className={`${handles.productsTable} w-full`}>
                  <thead className={`${handles.tableHeader} bg-gray-50`}>
                    <tr>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                        aria-sort={getAriaSort('name')}
                      >
                        <button
                          type="button"
                          onClick={() => handleSort('name')}
                          className="flex items-center gap-1 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 transition-colors hover:text-blue-600"
                        >
                          Produto
                          {renderSortIndicator('name')}
                        </button>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                        aria-sort={getAriaSort('brand')}
                      >
                        <button
                          type="button"
                          onClick={() => handleSort('brand')}
                          className="flex items-center gap-1 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 transition-colors hover:text-blue-600"
                        >
                          Marca
                          {renderSortIndicator('brand')}
                        </button>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Categoria
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                        aria-sort={getAriaSort('price')}
                      >
                        <button
                          type="button"
                          onClick={() => handleSort('price')}
                          className="flex items-center gap-1 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 transition-colors hover:text-blue-600"
                        >
                          Preço
                          {renderSortIndicator('price')}
                        </button>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                        aria-sort={getAriaSort('stock')}
                      >
                        <button
                          type="button"
                          onClick={() => handleSort('stock')}
                          className="flex items-center gap-1 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 transition-colors hover:text-blue-600"
                        >
                          Estoque
                          {renderSortIndicator('stock')}
                        </button>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                        aria-sort={getAriaSort('status')}
                      >
                        <button
                          type="button"
                          onClick={() => handleSort('status')}
                          className="flex items-center gap-1 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 transition-colors hover:text-blue-600"
                        >
                          Status
                          {renderSortIndicator('status')}
                        </button>
                      </th>
                      <th className="sticky right-0 z-20 bg-gray-50 px-2 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500 shadow-[inset_1px_0_0_0_rgb(226,232,240)] w-24">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {products.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-6 py-10 text-center text-sm text-gray-500">
                          Nenhum produto encontrado para os filtros selecionados.
                        </td>
                      </tr>
                    ) : (
                      products.map((product) => (
                        <tr key={product.id} className={`${handles.tableRow} hover:bg-gray-50`}>
                          <td className="whitespace-nowrap px-6 py-4">
                            <div className="flex items-center">
                              <div
                                className={`${handles.productImage} flex h-12 w-12 items-center justify-center overflow-hidden rounded-lg border border-gray-200 bg-gray-50`}
                              >
                                <ImageWithFallback
                                  src={
                                    product.images && product.images.length > 0
                                      ? (() => {
                                          const imageUrl = product.images[0]
                                          // Se a URL já é completa, usar diretamente, senão construir com base da API
                                          return buildImageUrl(imageUrl)
                                        })()
                                      : undefined
                                  }
                                  alt={product.name}
                                  className="h-full w-full object-cover"
                                  fallbackWrapperClassName="flex h-full w-full items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100"
                                  fallbackIconClassName="h-6 w-6 text-gray-300"
                                />
                              </div>
                              <div className={`${handles.productInfo} ml-4`}>
                                <div className={`${handles.productName} text-sm font-medium text-gray-900`}>
                                  {product.name}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {product.capacity != null && product.capacity > 0 ? `${product.capacity}Ah` : ''}
                                  {product.capacity != null && product.capacity > 0 && product.voltage != null && product.voltage > 0 ? ' - ' : ''}
                                  {product.voltage != null && product.voltage > 0 ? `${product.voltage}V` : ''}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-6 py-4">
                            <div className={`${handles.productBrand} text-sm text-gray-900`}>
                              {product.brand || '—'}
                            </div>
                            <div className="text-sm text-gray-500">
                              {product.model || '—'}
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-6 py-4">
                            <div className="text-sm text-gray-900">
                              {product.category?.name || '—'}
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-6 py-4">
                            <div className={`${handles.productPrice} text-sm font-medium text-gray-900`}>
                              {formatPrice(product.price)}
                            </div>
                            {product.originalPrice && (
                              <div className="text-sm text-gray-500 line-through">
                                {formatPrice(product.originalPrice)}
                              </div>
                            )}
                          </td>
                          <td className="whitespace-nowrap px-6 py-4">
                            <div className={`${handles.productStock} text-sm text-gray-900`}>
                              {product.stock} unidades
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-6 py-4">
                            <button
                              onClick={() => handleToggleStatus(product.id, product.isActive)}
                              disabled={pendingStatusToggle === product.id}
                              className={`${handles.statusToggle} ${
                                product.isActive
                                  ? `${handles.statusActive} bg-green-100 text-green-800`
                                  : `${handles.statusInactive} bg-red-100 text-red-800`
                              } rounded-full px-3 py-1 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
                                product.isActive ? 'focus-visible:ring-green-300' : 'focus-visible:ring-red-300'
                              } disabled:cursor-not-allowed disabled:opacity-60`}
                            >
                              {pendingStatusToggle === product.id
                                ? 'Atualizando...'
                                : product.isActive
                                ? 'Ativo'
                                : 'Inativo'}
                            </button>
                          </td>
                          <td className="sticky right-0 z-10 whitespace-nowrap bg-white px-2 py-4 text-sm font-medium shadow-[inset_1px_0_0_0_rgb(226,232,240)] w-32">
                            <div className={`${handles.productActions} flex space-x-2 justify-center`}>
                              <button
                                onClick={() => window.open(`/produto/${product.slug}`, '_blank')}
                                title="Ver produto"
                                aria-label="Ver produto"
                                className={`${handles.viewButton} inline-flex h-9 w-9 items-center justify-center rounded-md bg-blue-500 text-white shadow-sm transition-colors hover:bg-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-300 focus-visible:ring-offset-2`}
                              >
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                                <span className="sr-only">Ver produto</span>
                              </button>
                              <button
                                onClick={() => metadata && handleEditProduct(product)}
                                disabled={!metadata}
                                title="Editar produto"
                                aria-label="Editar produto"
                                className={`${handles.editButton} inline-flex h-9 w-9 items-center justify-center rounded-md bg-yellow-400 text-gray-900 shadow-sm transition-colors hover:bg-yellow-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-300 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60`}
                              >
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M16.862 3.487a2.1 2.1 0 112.971 2.97L7.5 18.79 3 21l2.21-4.5 11.652-12.013z" />
                                </svg>
                                <span className="sr-only">Editar produto</span>
                              </button>
                              <button
                                onClick={() => handleDeleteProduct(product)}
                                title="Excluir produto"
                                aria-label="Excluir produto"
                                disabled={pendingDeletion === product.id}
                                aria-busy={pendingDeletion === product.id}
                                className={`${handles.deleteButton} inline-flex h-9 w-9 items-center justify-center rounded-md bg-yellow-400 text-gray-900 shadow-sm transition-colors hover:bg-yellow-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-300 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60`}
                              >
                                {pendingDeletion === product.id ? (
                                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-700 border-t-transparent" aria-hidden="true" />
                                ) : (
                                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3m-4 0h14" />
                                  </svg>
                                )}
                                <span className="sr-only">
                                  {pendingDeletion === product.id ? 'Excluindo produto' : 'Excluir produto'}
                                </span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Paginação */}
              <TablePagination
                currentPage={currentPageNumber}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={limit}
                onPageChange={setCurrentPage}
                isLoading={isFetching}
                infoLabel="produtos"
                className={handles.pagination}
              />
            </div>

            {/* Modal de Produto */}
            {isModalOpen && (
              <ProductModal
                product={editingProduct}
                metadata={metadata}
                isSaving={isSavingProduct}
                onSave={handleSaveProduct}
                onDeleteImage={handleDeleteImageFromModal}
                onClose={() => {
                  setIsModalOpen(false)
                  setEditingProduct(null)
                }}
              />
            )}
          </div>
        </div>
      </div>
    </AdminLayout>

    <AdminFilterSidebar
      isOpen={isFilterSidebarOpen}
      onClose={handleCloseFilters}
      onApply={handleApplyFilters}
      onClear={handleClearFilters}
      isApplyDisabled={isApplyDisabled}
      applyLabel="Aplicar filtros"
      clearLabel="Limpar filtros"
    >
      <div className="space-y-6 pr-2">
        {/* Busca Geral */}
        <div>
          <label
            htmlFor="product-filter-search"
            className="mb-2 block text-sm font-medium text-gray-700"
          >
            Busca Geral
          </label>
          <Input
            id="product-filter-search"
            type="text"
            value={draftFilters.search}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              handleDraftFilterChange('search', event.target.value)
            }
            placeholder="Buscar em nome, marca ou modelo"
          />
        </div>

        {/* Nome */}
        <div>
          <label
            htmlFor="product-filter-name"
            className="mb-2 block text-sm font-medium text-gray-700"
          >
            Nome do Produto
          </label>
          <Input
            id="product-filter-name"
            type="text"
            value={draftFilters.name}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              handleDraftFilterChange('name', event.target.value)
            }
            placeholder="Digite o nome do produto"
          />
        </div>

        {/* Modelo */}
        <div>
          <label
            htmlFor="product-filter-model"
            className="mb-2 block text-sm font-medium text-gray-700"
          >
            Modelo
          </label>
          <Input
            id="product-filter-model"
            type="text"
            value={draftFilters.model}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              handleDraftFilterChange('model', event.target.value)
            }
            placeholder="Digite o modelo"
          />
        </div>

        {/* Marca */}
        <div>
          <label
            htmlFor="product-filter-brand"
            className="mb-2 block text-sm font-medium text-gray-700"
          >
            Marca
          </label>
          <Select
            id="product-filter-brand"
            name="product-filter-brand"
            value={draftFilters.brandId}
            onChange={(value) => handleDraftFilterChange('brandId', value || '')}
            options={[
              { value: '', label: 'Todas as marcas' },
              ...(metadata?.brands.map((brand) => ({
                value: brand.id,
                label: brand.name
              })) || [])
            ]}
            placeholder="Selecione uma marca"
            className="w-full"
          />
        </div>

        {/* Categoria */}
        <div>
          <label
            htmlFor="product-filter-category"
            className="mb-2 block text-sm font-medium text-gray-700"
          >
            Categoria
          </label>
          <Select
            id="product-filter-category"
            name="product-filter-category"
            value={draftFilters.categoryId}
            onChange={(value) => handleDraftFilterChange('categoryId', value || '')}
            options={[
              { value: '', label: 'Todas as categorias' },
              ...(metadata?.categories.map((category) => ({
                value: category.id,
                label: category.name
              })) || [])
            ]}
            placeholder="Selecione uma categoria"
            className="w-full"
          />
        </div>

        {/* Preço Mínimo e Máximo */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="product-filter-price-min"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              Preço Mínimo
            </label>
            <PriceInput
              id="product-filter-price-min"
              value={draftFilters.priceMin ? parseFloat(draftFilters.priceMin) : undefined}
              onChange={(value) =>
                handleDraftFilterChange('priceMin', value !== undefined ? value.toString() : '')
              }
              placeholder="Mínimo"
            />
          </div>
          <div>
            <label
              htmlFor="product-filter-price-max"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              Preço Máximo
            </label>
            <PriceInput
              id="product-filter-price-max"
              value={draftFilters.priceMax ? parseFloat(draftFilters.priceMax) : undefined}
              onChange={(value) =>
                handleDraftFilterChange('priceMax', value !== undefined ? value.toString() : '')
              }
              placeholder="Máximo"
            />
          </div>
        </div>

        {/* Estoque Mínimo e Máximo */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="product-filter-stock-min"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              Estoque Mínimo
            </label>
            <Input
              id="product-filter-stock-min"
              type="number"
              min="0"
              value={draftFilters.stockMin}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                handleDraftFilterChange('stockMin', event.target.value)
              }
              placeholder="0"
            />
          </div>
          <div>
            <label
              htmlFor="product-filter-stock-max"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              Estoque Máximo
            </label>
            <Input
              id="product-filter-stock-max"
              type="number"
              min="0"
              value={draftFilters.stockMax}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                handleDraftFilterChange('stockMax', event.target.value)
              }
              placeholder="∞"
            />
          </div>
        </div>

        {/* Capacidade Mínima e Máxima */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="product-filter-capacity-min"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              Capacidade Mínima (Ah)
            </label>
            <Input
              id="product-filter-capacity-min"
              type="number"
              step="0.1"
              min="0"
              value={draftFilters.capacityMin}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                handleDraftFilterChange('capacityMin', event.target.value)
              }
              placeholder="0"
            />
          </div>
          <div>
            <label
              htmlFor="product-filter-capacity-max"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              Capacidade Máxima (Ah)
            </label>
            <Input
              id="product-filter-capacity-max"
              type="number"
              step="0.1"
              min="0"
              value={draftFilters.capacityMax}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                handleDraftFilterChange('capacityMax', event.target.value)
              }
              placeholder="∞"
            />
          </div>
        </div>

        {/* Voltagem */}
        <div>
          <label
            htmlFor="product-filter-voltage"
            className="mb-2 block text-sm font-medium text-gray-700"
          >
            Voltagem (V)
          </label>
          <Input
            id="product-filter-voltage"
            type="number"
            step="0.1"
            min="0"
            value={draftFilters.voltage}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              handleDraftFilterChange('voltage', event.target.value)
            }
            placeholder="Ex: 12"
          />
        </div>

        {/* Status */}
        <div>
          <label
            htmlFor="product-filter-status"
            className="mb-2 block text-sm font-medium text-gray-700"
          >
            Status
          </label>
          <Select
            id="product-filter-status"
            name="product-filter-status"
            value={draftFilters.status}
            onChange={(value) =>
              handleDraftFilterChange(
                'status',
                value as AdminProductStatusFilter
              )
            }
            options={[
              { value: 'all', label: 'Todos os status' },
              { value: 'active', label: 'Ativos' },
              { value: 'inactive', label: 'Inativos' }
            ]}
            placeholder="Todos os status"
            className="w-full"
          />
        </div>
      </div>
    </AdminFilterSidebar>

    <ConfirmModal
      isOpen={deleteProductConfirmOpen}
      onClose={() => {
        if (pendingDeletion === null) {
          setDeleteProductConfirmOpen(false)
          setProductToDelete(null)
        }
      }}
      onConfirm={confirmDeleteProduct}
      title="Confirmar Exclusão"
      message={`Tem certeza que deseja excluir o produto "${productToDelete?.name}"? Esta ação não pode ser desfeita.`}
      confirmLabel="Excluir"
      cancelLabel="Cancelar"
      confirmButtonClassName="bg-red-600 hover:bg-red-700"
      isLoading={pendingDeletion !== null}
    />

    <ConfirmModal
      isOpen={deleteImageConfirmOpen}
      onClose={() => {
        setDeleteImageConfirmOpen(false)
        setImageToDelete(null)
      }}
      onConfirm={confirmDeleteImage}
      title="Confirmar Exclusão"
      message="Tem certeza que deseja excluir esta imagem? Esta ação não pode ser desfeita."
      confirmLabel="Excluir"
      cancelLabel="Cancelar"
      confirmButtonClassName="bg-red-600 hover:bg-red-700"
      isLoading={false}
    />
  </>
  )
}

// Componente Modal para adicionar/editar produto
interface ProductModalProps {
  product: Battery | null
  metadata: AdminProductMetadata | null
  isSaving: boolean
  onSave: (productData: AdminProductFormValues, imageFile?: File) => Promise<void>
  onClose: () => void
  onDeleteImage: (imageId: string) => Promise<void>
}

const ProductModal: React.FC<ProductModalProps> = ({
  product,
  metadata,
  isSaving,
  onSave,
  onClose,
  onDeleteImage
}) => {
  const handles = useCssHandles(CSS_HANDLES)
  const formId = useId()
  
  const {
    register,
    handleSubmit: handleFormSubmit,
    control,
    watch,
    reset: resetForm,
    formState: { errors }
  } = useForm<AdminProductFormValues>({
    defaultValues: {
      name: '',
      brandId: undefined,
      categoryId: metadata?.categories[0]?.id ?? '',
      model: '',
      price: 0,
      originalPrice: undefined,
      stock: 0,
      description: '',
      capacity: null,
      voltage: null,
      weight: null,
      length: null,
      width: null,
      height: null,
      isActive: true,
      isFeatured: false,
      scrapReturnEnabled: null
    }
  })

  const [submitError, setSubmitError] = useState<string | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [isLoadingDetails, setIsLoadingDetails] = useState(false)
  const [isLoadingImages, setIsLoadingImages] = useState(false)
  const [productImages, setProductImages] = useState<Array<{ id: string; imageUrl: string; isPrimary: boolean; sortOrder: number }>>([])
  const [newImageFiles, setNewImageFiles] = useState<File[]>([])
  const [newImagePreviews, setNewImagePreviews] = useState<string[]>([])
  const [isUploadingImages, setIsUploadingImages] = useState(false)
  const [settingPrimaryImageId, setSettingPrimaryImageId] = useState<string | null>(null)
  const loadingRef = useRef<string | null>(null)
  const loadedProductIdRef = useRef<string | null>(null)

  const initialFormValues = useMemo<AdminProductFormValues>(() => {
    const fallbackCategoryId = metadata?.categories[0]?.id ?? ''

    if (!product) {
      return {
        name: '',
        brandId: undefined,
        categoryId: fallbackCategoryId,
        model: '',
        price: 0,
        originalPrice: undefined,
        stock: 0,
        description: '',
        capacity: null,
        voltage: null,
        weight: null,
        length: null,
        width: null,
        height: null,
        isActive: true,
        isFeatured: false,
        scrapReturnEnabled: null
      }
    }

    const normalizedBrand = product.brand?.toLowerCase() ?? ''
    const brandId =
      product.brand
        ? metadata?.brands.find((brand) => brand.name.toLowerCase() === normalizedBrand)?.id ?? undefined
        : undefined

    const categoryId = product.category?.id ?? fallbackCategoryId

    // Função auxiliar para tratar campos opcionais numéricos
    // Preserva o 0 se ele existir, converte null/undefined para null
    const optionalNumber = (value: number | null | undefined): number | null => {
      if (value === null || value === undefined) return null
      return value
    }

    return {
      name: product.name,
      brandId,
      categoryId,
      model: product.model ?? '',
      price: product.price ?? 0,
      originalPrice: product.originalPrice && product.originalPrice > 0 ? product.originalPrice : undefined,
      stock: product.stock ?? 0,
      description: product.description ?? '',
      capacity: optionalNumber(product.capacity),
      voltage: optionalNumber(product.voltage),
      weight: optionalNumber((product as any).weight),
      length: optionalNumber((product as any).length),
      width: optionalNumber((product as any).width),
      height: optionalNumber((product as any).height),
      isActive: product.isActive ?? true,
      isFeatured: (product as any).isFeatured ?? false,
      scrapReturnEnabled: (product as any).scrapReturnEnabled ?? null
    }
  }, [product, metadata])

  useEffect(() => {
    resetForm(initialFormValues)
    setSubmitError(null)
    setNewImageFiles([])
    setNewImagePreviews([])
    setProductImages([])
    // Resetar o input file quando o modal abrir/fechar ou produto mudar
    const fileInputs = document.querySelectorAll('input[type="file"]')
    fileInputs.forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.value = ''
      }
    })
  }, [initialFormValues, product, resetForm])

  const loadProductImages = useCallback((imagesData: any[] | undefined) => {
    if (!imagesData || imagesData.length === 0) {
      setProductImages([])
      return
    }

    // O product.images agora vem como array de objetos com id, imageUrl, isPrimary, sortOrder
    const images = imagesData.map((img: string | { id?: string; imageUrl?: string; isPrimary?: boolean; sortOrder?: number }) => {
      const imageUrlString = typeof img === 'string' 
        ? img 
        : (typeof img === 'object' && img.imageUrl ? img.imageUrl : '')
      const fullUrl = buildImageUrl(imageUrlString) as string
      
      return {
        id: typeof img === 'object' && img.id ? img.id : `temp-${Date.now()}-${Math.random()}`,
        imageUrl: fullUrl,
        isPrimary: typeof img === 'object' && img.isPrimary !== undefined ? img.isPrimary : false,
        sortOrder: typeof img === 'object' && img.sortOrder !== undefined ? img.sortOrder : 0
      }
    })
    setProductImages(images)
  }, [])

  const refreshProductImages = useCallback(async (productId: string) => {
    try {
      setIsLoadingImages(true)
      const productDetail = await adminProductService.getById(productId)
      loadProductImages(productDetail.product.images)
    } catch (error) {
      console.error('Erro ao recarregar imagens do produto:', error)
    } finally {
      setIsLoadingImages(false)
    }
  }, [loadProductImages])

  const getErrorMessage = useCallback((err: unknown, fallback: string) => {
    if (err && typeof err === 'object') {
      const axiosLike = err as {
        response?: { data?: { message?: string; error?: string } }
        message?: string
      }
      const apiMessage =
        axiosLike.response?.data?.message ?? axiosLike.response?.data?.error
      if (apiMessage) {
        return apiMessage
      }
      if (axiosLike.message) {
        return axiosLike.message
      }
    }

    if (err instanceof Error && err.message) {
      return err.message
    }

    return fallback
  }, [])

  const loadProductDetails = useCallback(async (force = false) => {
    // Verificar se metadata existe (não precisa ser dependência)
    if (!metadata) {
      return
    }

    if (!product?.id) {
      return
    }

    // Evitar requisições duplicadas
    if (!force && (loadingRef.current === product.id || loadedProductIdRef.current === product.id)) {
      return
    }

    // Se for forçado, limpar referência para permitir nova requisição
    if (force) {
      loadedProductIdRef.current = null
    }

    // Marcar que está carregando este produto
    loadingRef.current = product.id
    setIsLoadingDetails(true)
    setLoadError(null)

    try {
      const detail = await adminProductService.getById(product.id)
      resetForm({
        ...detail.form,
        originalPrice: detail.form.originalPrice ?? undefined
      })
      // Carregar imagens usando os dados já carregados
      loadProductImages(detail.product.images)
      // Marcar como carregado
      loadedProductIdRef.current = product.id
    } catch (err) {
      console.error('Erro ao carregar detalhes do produto:', err)
      setLoadError(
        getErrorMessage(
          err,
          'Não foi possível carregar os dados completos do produto.'
        )
      )
      // Limpar referência em caso de erro para permitir nova tentativa
      loadedProductIdRef.current = null
    } finally {
      setIsLoadingDetails(false)
      loadingRef.current = null
    }
  }, [product?.id, getErrorMessage, loadProductImages])

  const retryLoadProductDetails = useCallback(() => {
    void loadProductDetails(true)
  }, [loadProductDetails])

  // Memoizar uma flag que indica se metadata está disponível (evita mudanças desnecessárias)
  const hasMetadata = useMemo(() => !!metadata?.categories?.length, [metadata?.categories?.length])

  useEffect(() => {
    // Resetar quando não houver produto
    if (!product?.id) {
      setIsLoadingDetails(false)
      setLoadError(null)
      loadingRef.current = null
      loadedProductIdRef.current = null
      return
    }

    // Verificar se metadata existe (espera até estar disponível)
    if (!hasMetadata) {
      return
    }

    // Evitar requisições duplicadas - só carregar se ainda não foi carregado
    if (loadedProductIdRef.current === product.id) {
      return
    }

    void loadProductDetails()
  }, [product?.id, hasMetadata, loadProductDetails])
  
  // Efeito específico para recarregar imagens quando o produto for atualizado
  useEffect(() => {
    if (product?.id && product.images) {
      // Quando as imagens do produto mudarem, recarregar as imagens no estado local
      loadProductImages(product.images)
      // Resetar o loadedProductIdRef para permitir recarregamento se necessário
      if (loadedProductIdRef.current === product.id) {
        // Forçar recarregamento das imagens
        loadedProductIdRef.current = null
      }
    }
  }, [product?.images, product?.id, loadProductImages])

  const handleFileInputChangeMultiple = (files: File[]) => {
    if (files && files.length > 0) {
      setNewImageFiles((prev) => [...prev, ...files])
      
      // Criar previews
      files.forEach((file) => {
        const reader = new FileReader()
        reader.onloadend = () => {
          if (reader.result) {
            setNewImagePreviews((prev) => [...prev, reader.result as string])
          }
        }
        reader.onerror = () => {
          console.error('Erro ao ler arquivo:', file.name)
        }
        reader.readAsDataURL(file)
      })
    }
  }

  const handleFileInputChange = (file: File | null) => {
    if (file) {
      setNewImageFiles((prev) => [...prev, file])
      
      // Criar preview
      const reader = new FileReader()
      reader.onloadend = () => {
        if (reader.result) {
          setNewImagePreviews((prev) => [...prev, reader.result as string])
        }
      }
      reader.onerror = () => {
        console.error('Erro ao ler arquivo:', file.name)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleRemoveNewImage = (index: number) => {
    setNewImageFiles((prev) => prev.filter((_, i) => i !== index))
    setNewImagePreviews((prev) => prev.filter((_, i) => i !== index))
  }

  const handleDeleteImage = async (imageId: string) => {
    if (!product?.id) return
    try {
      await onDeleteImage(imageId)
      // O recarregamento será feito quando o editingProduct for atualizado no componente principal
    } catch (error) {
      console.error('Erro ao deletar imagem:', error)
    }
  }

  const handleSetPrimary = async (imageId: string) => {
    if (!product?.id) return
    
    setSettingPrimaryImageId(imageId)
    try {
      await adminProductService.setPrimaryImage(product.id, imageId)
      await refreshProductImages(product.id)
    } catch (error) {
      console.error('Erro ao definir imagem primária:', error)
      setSubmitError('Erro ao definir imagem primária. Tente novamente.')
    } finally {
      setSettingPrimaryImageId(null)
    }
  }

  const handleMoveImage = async (fromIndex: number, toIndex: number) => {
    if (!product?.id) return
    
    const newImages = [...productImages]
    const [moved] = newImages.splice(fromIndex, 1)
    newImages.splice(toIndex, 0, moved)
    
    // Atualizar sortOrder
    const imageIds = newImages.map((img) => img.id)
    
    try {
      await adminProductService.reorderImages(product.id, imageIds)
      await refreshProductImages(product.id)
    } catch (error) {
      console.error('Erro ao reordenar imagens:', error)
      setSubmitError('Erro ao reordenar imagens. Tente novamente.')
    }
  }

  const handleUploadNewImages = async () => {
    if (!product?.id || newImageFiles.length === 0) return
    
    setIsUploadingImages(true)
    try {
      for (const file of newImageFiles) {
        await adminProductService.uploadImage(product.id, file)
      }
      setNewImageFiles([])
      setNewImagePreviews([])
      await refreshProductImages(product.id)
    } catch (error) {
      console.error('Erro ao fazer upload das imagens:', error)
      setSubmitError('Erro ao fazer upload das imagens. Tente novamente.')
    } finally {
      setIsUploadingImages(false)
    }
  }

  const onSubmit = async (data: AdminProductFormValues) => {
    setSubmitError(null)

    if (!metadata) {
      setSubmitError('Carregue as informações de marca e categoria antes de salvar.')
      return
    }

    // Validação: estoque e preço são obrigatórios
    if (data.stock === undefined || data.stock === null) {
      setSubmitError('O campo Estoque é obrigatório.')
      return
    }

    if (data.stock < 0) {
      setSubmitError('O campo Estoque não pode ser negativo.')
      return
    }

    if (data.price === 0 || data.price === undefined || data.price === null) {
      setSubmitError('O campo Preço é obrigatório e deve ser maior que zero.')
      return
    }

    try {
      // Para novo produto, usar a primeira imagem se houver
      const firstImageFile = newImageFiles.length > 0 ? newImageFiles[0] : undefined
      await onSave(data, firstImageFile)
      
      // Se houver mais imagens e o produto foi criado, fazer upload delas
      // Nota: isso será feito após o produto ser criado, então precisamos aguardar o callback
    } catch (err) {
      setSubmitError(
        getErrorMessage(
          err,
          'Não foi possível salvar o produto. Verifique os dados e tente novamente.'
        )
      )
    }
  }

  const isMetadataReady = Boolean(
    metadata && metadata.brands.length > 0 && metadata.categories.length > 0
  )
  const isFormDisabled = isLoadingDetails || !isMetadataReady

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={product ? 'Editar Produto' : 'Adicionar Produto'}
      size="lg"
      overlayClassName={`${handles.modal} ${handles.modalOverlay}`}
      contentClassName={`${handles.modalContent} w-full max-w-2xl`}
      headerClassName={`${handles.modalHeader}`}
      titleClassName={handles.modalTitle}
      closeButtonClassName={handles.modalClose}
      bodyClassName={handles.modalBody}
      bodyStyle={{ padding: 0 }}
      footer={
        <div className="flex w-full justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className={`${handles.cancelButton} px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors`}
            disabled={isSaving}
          >
            Cancelar
          </button>
          <button
            type="submit"
            form={formId}
            disabled={isSaving || isFormDisabled}
            className={`${handles.saveButton} px-4 py-2 bg-blue-600 text-white rounded-lg transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60`}
          >
            {isSaving ? 'Salvando...' : product ? 'Salvar Alterações' : 'Adicionar Produto'}
          </button>
        </div>
      }
    >
      <form id={formId} onSubmit={handleFormSubmit(onSubmit)}>
        <div className="p-6 space-y-4">
          {!isMetadataReady && (
            <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800">
              Carregue marcas e categorias antes de cadastrar produtos.
            </div>
          )}

          {loadError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                <span>{loadError}</span>
                <button
                  type="button"
                  onClick={retryLoadProductDetails}
                  className="underline hover:text-red-800"
                >
                  Tentar novamente
                </button>
              </div>
            </div>
          )}

          {isLoadingDetails ? (
            <div className="space-y-4">
              {/* Indicador de carregamento */}
              <div className="flex items-center justify-center py-4">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
                  <span>Carregando dados do produto...</span>
                </div>
              </div>

              {/* Skeleton para primeira linha: Nome e Marca */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-pulse">
                <div>
                  <div className="h-4 w-24 bg-gray-200 rounded mb-2" />
                  <div className="h-10 w-full bg-gray-200 rounded-lg" />
                </div>
                <div>
                  <div className="h-4 w-16 bg-gray-200 rounded mb-2" />
                  <div className="h-10 w-full bg-gray-200 rounded-lg" />
                </div>
              </div>

              {/* Skeleton para segunda linha: Categoria e Modelo */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-pulse">
                <div>
                  <div className="h-4 w-20 bg-gray-200 rounded mb-2" />
                  <div className="h-10 w-full bg-gray-200 rounded-lg" />
                </div>
                <div>
                  <div className="h-4 w-16 bg-gray-200 rounded mb-2" />
                  <div className="h-10 w-full bg-gray-200 rounded-lg" />
                </div>
              </div>

              {/* Skeleton para terceira linha: Capacidade, Voltagem e Estoque */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 animate-pulse">
                <div>
                  <div className="h-4 w-28 bg-gray-200 rounded mb-2" />
                  <div className="h-10 w-full bg-gray-200 rounded-lg" />
                </div>
                <div>
                  <div className="h-4 w-20 bg-gray-200 rounded mb-2" />
                  <div className="h-10 w-full bg-gray-200 rounded-lg" />
                </div>
                <div>
                  <div className="h-4 w-16 bg-gray-200 rounded mb-2" />
                  <div className="h-10 w-full bg-gray-200 rounded-lg" />
                </div>
              </div>

              {/* Skeleton para quarta linha: Preço e Preço Original */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-pulse">
                <div>
                  <div className="h-4 w-12 bg-gray-200 rounded mb-2" />
                  <div className="h-10 w-full bg-gray-200 rounded-lg" />
                </div>
                <div>
                  <div className="h-4 w-24 bg-gray-200 rounded mb-2" />
                  <div className="h-10 w-full bg-gray-200 rounded-lg" />
                </div>
              </div>

              {/* Skeleton para Foto do Produto */}
              <div className="animate-pulse">
                <div className="h-4 w-28 bg-gray-200 rounded mb-2" />
                <div className="h-10 w-full bg-gray-200 rounded-lg mb-2" />
                <div className="h-32 w-32 bg-gray-200 rounded-lg" />
                <div className="h-3 w-64 bg-gray-200 rounded mt-2" />
              </div>

              {/* Skeleton para Descrição */}
              <div className="animate-pulse">
                <div className="h-4 w-20 bg-gray-200 rounded mb-2" />
                <div className="h-24 w-full bg-gray-200 rounded-lg" />
              </div>

              {/* Skeleton para Checkbox */}
              <div className="flex items-center gap-3 animate-pulse">
                <div className="h-4 w-4 bg-gray-200 rounded" />
                <div className="h-4 w-24 bg-gray-200 rounded" />
              </div>
            </div>
          ) : (
            <>
              <div className={`${handles.formRow} grid grid-cols-1 md:grid-cols-2 gap-4`}>
            <div className={`${handles.formGroup}`}>
              <label className={`${handles.formLabel} block text-sm font-medium text-gray-700 mb-1`}>
                Nome do Produto *
              </label>
              <Input
                type="text"
                {...register('name', { required: 'Nome do produto é obrigatório' })}
                disabled={isFormDisabled}
                error={!!errors.name}
                helperText={errors.name?.message}
              />
            </div>
            <div className={`${handles.formGroup}`}>
              <label className={`${handles.formLabel} block text-sm font-medium text-gray-700 mb-1`}>
                Marca
              </label>
              <Controller
                name="brandId"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Select
                    value={value || ''}
                    onChange={(val) => onChange(val === '' ? undefined : val)}
                    options={[
                      { value: '', label: 'Sem marca' },
                      ...(metadata?.brands.map((brand) => ({
                        value: brand.id,
                        label: brand.name
                      })) || [])
                    ]}
                    placeholder="Sem marca"
                    disabled={isFormDisabled}
                    className={`${handles.formSelect}`}
                  />
                )}
              />
            </div>
          </div>

          <div className={`${handles.formRow} grid grid-cols-1 md:grid-cols-2 gap-4`}>
            <div className={`${handles.formGroup}`}>
              <label className={`${handles.formLabel} block text-sm font-medium text-gray-700 mb-1`}>
                Categoria *
              </label>
              <Controller
                name="categoryId"
                control={control}
                rules={{ required: 'Categoria é obrigatória' }}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <>
                    <Select
                      value={value}
                      onChange={onChange}
                      options={[
                        { value: '', label: 'Selecione uma categoria' },
                        ...(metadata?.categories.map((category) => ({
                          value: category.id,
                          label: category.name
                        })) || [])
                      ]}
                      placeholder="Selecione uma categoria"
                      disabled={isFormDisabled}
                      className={`${handles.formSelect}`}
                    />
                    {error && (
                      <p className="mt-1 text-xs text-red-600">{error.message}</p>
                    )}
                  </>
                )}
              />
            </div>
            <div className={`${handles.formGroup}`}>
              <label className={`${handles.formLabel} block text-sm font-medium text-gray-700 mb-1`}>
                Modelo
              </label>
              <Input
                type="text"
                {...register('model')}
                disabled={isFormDisabled}
              />
            </div>
          </div>

          <div className={`${handles.formRow} grid grid-cols-1 md:grid-cols-3 gap-4`}>
            <div className={`${handles.formGroup}`}>
              <label className={`${handles.formLabel} block text-sm font-medium text-gray-700 mb-1`}>
                Capacidade (Ah)
              </label>
              <Controller
                name="capacity"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Input
                    type="number"
                    min={0}
                    step="0.1"
                    value={value !== null && value !== undefined ? value : ''}
                    onChange={(e) => {
                      const val = e.target.value
                      onChange(val === '' ? null : (Number(val) >= 0 ? Number(val) : null))
                    }}
                    disabled={isFormDisabled}
                  />
                )}
              />
            </div>
            <div className={`${handles.formGroup}`}>
              <label className={`${handles.formLabel} block text-sm font-medium text-gray-700 mb-1`}>
                Voltagem (V)
              </label>
              <Controller
                name="voltage"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Input
                    type="number"
                    min={0}
                    step="0.1"
                    value={value !== null && value !== undefined ? value : ''}
                    onChange={(e) => {
                      const val = e.target.value
                      onChange(val === '' ? null : (Number(val) >= 0 ? Number(val) : null))
                    }}
                    disabled={isFormDisabled}
                  />
                )}
              />
            </div>
            <div className={`${handles.formGroup}`}>
              <label className={`${handles.formLabel} block text-sm font-medium text-gray-700 mb-1`}>
                Estoque *
              </label>
              <Controller
                name="stock"
                control={control}
                rules={{
                  required: 'Estoque é obrigatório',
                  min: { value: 0, message: 'Estoque não pode ser negativo' }
                }}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <>
                    <Input
                      type="number"
                      min={0}
                      value={value ?? ''}
                      onChange={(e) => {
                        const val = e.target.value
                        onChange(val === '' ? 0 : Math.max(0, Number(val) || 0))
                      }}
                      disabled={isFormDisabled}
                      error={!!error}
                      helperText={error?.message}
                    />
                  </>
                )}
              />
            </div>
          </div>

          <div className={`${handles.formRow} grid grid-cols-1 md:grid-cols-4 gap-4`}>
            <div className={`${handles.formGroup}`}>
              <label className={`${handles.formLabel} block text-sm font-medium text-gray-700 mb-1`}>
                Peso (kg)
              </label>
              <Controller
                name="weight"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <DecimalInput
                    value={value}
                    onChange={onChange}
                    disabled={isFormDisabled}
                    decimalPlaces={2}
                  />
                )}
              />
            </div>
            <div className={`${handles.formGroup}`}>
              <label className={`${handles.formLabel} block text-sm font-medium text-gray-700 mb-1`}>
                Comprimento (cm)
              </label>
              <Controller
                name="length"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <DecimalInput
                    value={value}
                    onChange={onChange}
                    disabled={isFormDisabled}
                    decimalPlaces={2}
                  />
                )}
              />
            </div>
            <div className={`${handles.formGroup}`}>
              <label className={`${handles.formLabel} block text-sm font-medium text-gray-700 mb-1`}>
                Largura (cm)
              </label>
              <Controller
                name="width"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <DecimalInput
                    value={value}
                    onChange={onChange}
                    disabled={isFormDisabled}
                    decimalPlaces={2}
                  />
                )}
              />
            </div>
            <div className={`${handles.formGroup}`}>
              <label className={`${handles.formLabel} block text-sm font-medium text-gray-700 mb-1`}>
                Altura (cm)
              </label>
              <Controller
                name="height"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <DecimalInput
                    value={value}
                    onChange={onChange}
                    disabled={isFormDisabled}
                    decimalPlaces={2}
                  />
                )}
              />
            </div>
          </div>

          <div className={`${handles.formRow} grid grid-cols-1 md:grid-cols-2 gap-4`}>
            <div className={`${handles.formGroup}`}>
              <label className={`${handles.formLabel} block text-sm font-medium text-gray-700 mb-1`}>
                Preço *
              </label>
              <Controller
                name="price"
                control={control}
                rules={{
                  required: 'Preço é obrigatório',
                  min: { value: 0.01, message: 'Preço deve ser maior que zero' }
                }}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <>
                    <PriceInput
                      value={value}
                      onChange={onChange}
                      disabled={isFormDisabled}
                      error={!!error}
                      helperText={error?.message}
                    />
                  </>
                )}
              />
            </div>
            <div className={`${handles.formGroup}`}>
              <label className={`${handles.formLabel} block text-sm font-medium text-gray-700 mb-1`}>
                Preço Original
              </label>
              <Controller
                name="originalPrice"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <PriceInput
                    value={value}
                    onChange={onChange}
                    disabled={isFormDisabled}
                  />
                )}
              />
            </div>
          </div>

          <div className={`${handles.formGroup}`}>
            <label className={`${handles.formLabel} block text-sm font-medium text-gray-700 mb-1`}>
              Fotos do Produto
            </label>
            <div className="space-y-4">
              {/* Imagens existentes */}
              {isLoadingImages && product?.id ? (
                <div className="space-y-2">
                  <p className="text-sm text-gray-600">Carregando imagens...</p>
                  <div className="grid grid-cols-4 gap-3">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className="relative aspect-square rounded-lg overflow-hidden border-2 border-gray-200 animate-pulse bg-gray-200" />
                    ))}
                  </div>
                </div>
              ) : productImages.length > 0 ? (
                <div className="space-y-2">
                  <p className="text-sm text-gray-600">Imagens existentes:</p>
                  <div className="grid grid-cols-4 gap-3">
                    {productImages.map((img, index) => (
                      <div key={img.id} className="relative group">
                        <div className="relative aspect-square rounded-lg overflow-hidden border-2 border-gray-200">
                          {settingPrimaryImageId === img.id && (
                            <div className="absolute inset-0 bg-black bg-opacity-50 z-10 flex items-center justify-center">
                              <div className="flex flex-col items-center gap-2">
                                <div className="h-8 w-8 animate-spin rounded-full border-4 border-white border-t-transparent" />
                                <span className="text-white text-xs">Definindo como principal...</span>
                              </div>
                            </div>
                          )}
                          <img
                            src={img.imageUrl}
                            alt={`Imagem ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                          {img.isPrimary && (
                            <div className="absolute top-1 left-1 bg-blue-500 text-white text-xs px-2 py-1 rounded">
                              Principal
                            </div>
                          )}
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-opacity flex items-center justify-center gap-2">
                            {index > 0 && (
                              <button
                                type="button"
                                onClick={() => handleMoveImage(index, index - 1)}
                                className="opacity-0 group-hover:opacity-100 bg-white text-gray-700 p-2 rounded hover:bg-gray-100"
                                title="Mover para esquerda"
                              >
                                ←
                              </button>
                            )}
                            {!img.isPrimary && (
                              <button
                                type="button"
                                onClick={() => handleSetPrimary(img.id)}
                                disabled={settingPrimaryImageId !== null}
                                className="opacity-0 group-hover:opacity-100 bg-white text-gray-700 p-2 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Definir como principal"
                              >
                                {settingPrimaryImageId === img.id ? (
                                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-500 border-t-transparent" />
                                ) : (
                                  '⭐'
                                )}
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => handleDeleteImage(img.id)}
                              className="opacity-0 group-hover:opacity-100 bg-red-500 text-white p-2 rounded hover:bg-red-600"
                              title="Deletar"
                            >
                              ×
                            </button>
                            {index < productImages.length - 1 && (
                              <button
                                type="button"
                                onClick={() => handleMoveImage(index, index + 1)}
                                className="opacity-0 group-hover:opacity-100 bg-white text-gray-700 p-2 rounded hover:bg-gray-100"
                                title="Mover para direita"
                              >
                                →
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : product?.id ? (
                <div className="space-y-2">
                  <p className="text-sm text-gray-600">Nenhuma imagem encontrada</p>
                </div>
              ) : null}

              {/* Adicionar novas imagens */}
              {product?.id && (
                <div className="space-y-2">
                  <p className="text-sm text-gray-600">Adicionar novas imagens:</p>
                  <FileInput
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    multiple
                    onChangeMultiple={handleFileInputChangeMultiple}
                    disabled={isFormDisabled || isUploadingImages}
                    showPreview={false}
                  />
                  
                  {/* Preview das novas imagens */}
                  {newImagePreviews.length > 0 && (
                    <div className="grid grid-cols-4 gap-3 mt-2">
                      {newImagePreviews.map((preview, index) => (
                        <div key={index} className="relative">
                          <div className="relative aspect-square rounded-lg overflow-hidden border-2 border-blue-300">
                            <img
                              src={preview}
                              alt={`Preview ${index + 1}`}
                              className="w-full h-full object-cover"
                            />
                            <button
                              type="button"
                              onClick={() => handleRemoveNewImage(index)}
                              className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                              title="Remover"
                            >
                              ×
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {newImageFiles.length > 0 && (
                    <button
                      type="button"
                      onClick={handleUploadNewImages}
                      disabled={isUploadingImages || isFormDisabled}
                      className="mt-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isUploadingImages ? 'Enviando...' : `Enviar ${newImageFiles.length} imagem(ns)`}
                    </button>
                  )}
                </div>
              )}

              {/* Para criação de novo produto, permitir upload de uma imagem inicial */}
              {!product?.id && (
                <div className="space-y-2">
                  <FileInput
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={handleFileInputChange}
                    disabled={isFormDisabled}
                    showPreview={false}
                  />
                  
                  {newImagePreviews.length > 0 && (
                    <div className="grid grid-cols-4 gap-3 mt-2">
                      {newImagePreviews.map((preview, index) => (
                        <div key={index} className="relative">
                          <div className="relative aspect-square rounded-lg overflow-hidden border-2 border-blue-300">
                            <img
                              src={preview}
                              alt={`Preview ${index + 1}`}
                              className="w-full h-full object-cover"
                            />
                            <button
                              type="button"
                              onClick={() => handleRemoveNewImage(index)}
                              className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                              title="Remover"
                            >
                              ×
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <p className="text-xs text-gray-500">
                Formatos aceitos: JPEG, PNG, WEBP. Tamanho máximo: 5MB por imagem
              </p>
            </div>
          </div>

          <div className={`${handles.formGroup}`}>
            <label className={`${handles.formLabel} block text-sm font-medium text-gray-700 mb-1`}>
              Descrição *
            </label>
            <Textarea
              {...register('description', { required: 'Descrição é obrigatória' })}
              rows={4}
              disabled={isFormDisabled}
              error={!!errors.description}
              helperText={errors.description?.message}
            />
          </div>

          <Controller
            name="isActive"
            control={control}
            render={({ field: { onChange, value, name, ref } }) => (
              <Checkbox
                id="isActive"
                ref={ref}
                name={name}
                checked={value ?? true}
                onChange={(e) => onChange(e.target.checked)}
                label="Produto ativo"
                disabled={isFormDisabled}
              />
            )}
          />

          <Controller
            name="isFeatured"
            control={control}
            render={({ field: { onChange, value, name, ref } }) => (
              <Checkbox
                id="isFeatured"
                ref={ref}
                name={name}
                checked={value ?? false}
                onChange={(e) => onChange(e.target.checked)}
                label="Produto em destaque"
                disabled={isFormDisabled}
              />
            )}
          />

          {metadata?.categories?.find((c) => c.id === watch('categoryId'))?.allowsScrapReturn && (
            <Controller
              name="scrapReturnEnabled"
              control={control}
              render={({ field: { onChange, value, name, ref } }) => (
                <Checkbox
                  id="scrapReturnDisabled"
                  ref={ref}
                  name={name}
                  checked={value === false}
                  onChange={(e) => onChange(e.target.checked ? false : null)}
                  label="Desativar opção de troca de sucata para este produto"
                  disabled={isFormDisabled}
                />
              )}
            />
          )}

          {submitError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {submitError}
            </div>
          )}
            </>
          )}
        </div>
      </form>
    </Modal>
  )
}

export default AdminProducts
